/**
 * threadListCopy — the rail / sheet / category chrome, in the viewer's
 * language (2026-09-09). House voice: lowercase, terse; "Prest0n" keeps its
 * capital where it appears. Chrome only — never content (D9).
 */
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';
import type { ThreadListLang } from '../../types/threadCategories';

export interface ThreadListCopy {
  routines: string;
  noCategory: string;
  newCategory: string;
  newCategoryPlaceholder: string;
  moveTo: (title: string) => string;
  filedUnder: (section: string) => string;
  moveThread: (title: string) => string;
  threadActions: (title: string) => string;
  sectionActions: (section: string) => string;
  rename: string;
  renameSection: string;
  deleteSection: string;
  moveUp: string;
  moveDown: string;
  collapse: string;
  expand: string;
  manageSections: string;
  /** The sections manager: its heading, its close control, the empty-section count. */
  sectionsHeading: string;
  done: string;
  emptySection: string;
  pin: string;
  unpin: string;
  archive: string;
  movedTo: (section: string) => string;
  sectionRemoved: (section: string) => string;
  sectionRenamed: (section: string) => string;
  undo: string;
  nameTaken: string;
  dropHere: string;
  sectionNamePlaceholder: string;
  // ── search (PR-C, 2026-09-09: the search that suggests) ──
  searchPlaceholder: string;
  searchLabel: string;
  searchTooltip: (modKey: string) => string;
  clearSearch: string;
  jumpBackIn: string;
  recentSearches: string;
  threadsGroup: string;
  messagesGroup: string;
  filesGroup: string;
  routinesGroup: string;
  searchingEverywhere: string;
  searchFailed: string;
  retry: string;
  nothingMatches: string;
  nothingMatchesAnywhere: string;
  wasNamed: (name: string) => string;
  /** The opening of a former-name line, for a highlighted name: `was “` … `”`. */
  wasNamedPrefix: string;
  you: string;
  prest0n: string;
  nextRun: (when: string) => string;
  unread: string;
}

const COPY: Record<ThreadListLang, ThreadListCopy> = {
  en: {
    routines: 'routines',
    noCategory: 'no category',
    newCategory: 'new category…',
    newCategoryPlaceholder: 'new category…',
    moveTo: (title) => `move ${title ? `“${title}”` : 'this thread'} to…`,
    filedUnder: (section) => `filed under ${section}`,
    moveThread: (title) => `move thread ${title} to a section`,
    threadActions: (title) => `actions for ${title}`,
    sectionActions: (section) => `actions for the ${section} section`,
    rename: 'rename',
    renameSection: 'rename section',
    deleteSection: 'delete section',
    moveUp: 'move up',
    moveDown: 'move down',
    collapse: 'collapse',
    expand: 'expand',
    manageSections: 'manage sections…',
    sectionsHeading: 'sections',
    done: 'done',
    emptySection: 'empty',
    pin: 'pin to top',
    unpin: 'unpin',
    archive: 'archive',
    movedTo: (section) => `moved to ${section}`,
    sectionRemoved: (section) => `“${section}” removed — its threads are back in their own sections`,
    sectionRenamed: (section) => `renamed to ${section}`,
    undo: 'undo',
    nameTaken: 'a section already has that name',
    dropHere: 'drop here',
    sectionNamePlaceholder: 'section name',
    searchPlaceholder: 'search threads, messages, files…',
    searchLabel: 'search threads, messages, files and routines',
    searchTooltip: (modKey) => `search · ${modKey}K`,
    clearSearch: 'clear search',
    jumpBackIn: 'jump back in',
    recentSearches: 'recent searches',
    threadsGroup: 'threads',
    messagesGroup: 'messages',
    filesGroup: 'files',
    routinesGroup: 'routines',
    searchingEverywhere: 'searching everywhere…',
    searchFailed: 'couldn’t search everything — check your connection',
    retry: 'retry',
    nothingMatches: 'nothing here matches yet',
    nothingMatchesAnywhere: 'nothing matches — anywhere',
    wasNamed: (name) => `was “${name}”`,
    wasNamedPrefix: 'was “',
    you: 'you',
    prest0n: 'Prest0n',
    nextRun: (when) => `next run ${when}`,
    unread: 'unread',
  },
  es: {
    routines: 'rutinas',
    noCategory: 'sin categoría',
    newCategory: 'nueva categoría…',
    newCategoryPlaceholder: 'nueva categoría…',
    moveTo: (title) => `mover ${title ? `“${title}”` : 'este hilo'} a…`,
    filedUnder: (section) => `guardado en ${section}`,
    moveThread: (title) => `mover el hilo ${title} a una sección`,
    threadActions: (title) => `acciones para ${title}`,
    sectionActions: (section) => `acciones para la sección ${section}`,
    rename: 'renombrar',
    renameSection: 'renombrar sección',
    deleteSection: 'eliminar sección',
    moveUp: 'subir',
    moveDown: 'bajar',
    collapse: 'contraer',
    expand: 'expandir',
    manageSections: 'gestionar secciones…',
    sectionsHeading: 'secciones',
    done: 'listo',
    emptySection: 'vacía',
    pin: 'fijar arriba',
    unpin: 'desfijar',
    archive: 'archivar',
    movedTo: (section) => `movido a ${section}`,
    sectionRemoved: (section) => `“${section}” eliminada — sus hilos vuelven a sus propias secciones`,
    sectionRenamed: (section) => `renombrada a ${section}`,
    undo: 'deshacer',
    nameTaken: 'ya hay una sección con ese nombre',
    dropHere: 'suelta aquí',
    sectionNamePlaceholder: 'nombre de la sección',
    searchPlaceholder: 'buscar hilos, mensajes, archivos…',
    searchLabel: 'buscar hilos, mensajes, archivos y rutinas',
    searchTooltip: (modKey) => `buscar · ${modKey}K`,
    clearSearch: 'borrar la búsqueda',
    jumpBackIn: 'retomar',
    recentSearches: 'búsquedas recientes',
    threadsGroup: 'hilos',
    messagesGroup: 'mensajes',
    filesGroup: 'archivos',
    routinesGroup: 'rutinas',
    searchingEverywhere: 'buscando en todo…',
    searchFailed: 'no se pudo buscar en todo — revisa tu conexión',
    retry: 'reintentar',
    nothingMatches: 'nada coincide todavía',
    nothingMatchesAnywhere: 'nada coincide — en ningún sitio',
    wasNamed: (name) => `antes “${name}”`,
    wasNamedPrefix: 'antes “',
    you: 'tú',
    prest0n: 'Prest0n',
    nextRun: (when) => `próxima ejecución ${when}`,
    unread: 'sin leer',
  },
};

export function threadListCopy(lang: ThreadListLang): ThreadListCopy {
  return COPY[lang] ?? COPY.en;
}

/** The viewer's list language (the platform's UI-language resolver). */
export function resolveThreadListLang(): ThreadListLang {
  return getUserUiLanguage() === 'es' ? 'es' : 'en';
}

export const THREAD_LIST_COPY_KEYS = Object.keys(COPY.en) as Array<keyof ThreadListCopy>;
